import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_PERMISSION_DENIED_EVENT } from '../../api/useApi';
import { API_EMPTY_SCOPE_EVENT } from '../../api/axios-client';
import { useStateContext } from '../../contexts/ContextProvider';
import { usePermissions } from '../../contexts/PermissionContext';
import { findFirstAccessibleRoute, FALLBACK_ROUTE } from '../../config/routePermissions';

/**
 * Composant invisible qui écoute les événements 'permission-denied'
 * et redirige l'utilisateur vers la première page accessible.
 */
export default function PermissionErrorHandler() {
    const navigate = useNavigate();
    const { isUserLoading, user, currentFacility } = useStateContext();
    const { hasPermission, hasAnyPermission, isSuperAdmin } = usePermissions();

    useEffect(() => {
        const handlePermissionDenied = () => {
            // Ne pas gérer si l'utilisateur n'est pas encore chargé
            if (isUserLoading || user.id === 0) return;

            // Trouver la première route accessible et rediriger silencieusement
            const firstAccessible = getFirstAccessibleRoute();
            navigate(firstAccessible, { replace: true });
        };

        window.addEventListener(API_PERMISSION_DENIED_EVENT, handlePermissionDenied);
        return () => window.removeEventListener(API_PERMISSION_DENIED_EVENT, handlePermissionDenied);
    }, [navigate, isUserLoading, user.id, currentFacility, hasPermission, hasAnyPermission, isSuperAdmin]);

    // Périmètre vide : écran dédié (sans boucle si on y est déjà).
    useEffect(() => {
        const handleEmptyScope = () => {
            if (window.location.pathname !== '/empty-scope') navigate('/empty-scope', { replace: true });
        };
        window.addEventListener(API_EMPTY_SCOPE_EVENT, handleEmptyScope);
        return () => window.removeEventListener(API_EMPTY_SCOPE_EVENT, handleEmptyScope);
    }, [navigate]);

    /**
     * Trouve la première route accessible pour l'utilisateur
     */
    function getFirstAccessibleRoute(): string {
        // Super admin redirigé vers le dashboard
        if (isSuperAdmin) return '/dashboard';

        const route = findFirstAccessibleRoute(currentFacility, (perm) => (
            Array.isArray(perm) ? hasAnyPermission(perm) : hasPermission(perm)
        ));
        if (route) return route;

        return FALLBACK_ROUTE;
    }

    // Composant invisible - ne rend rien
    return null;
}
